import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    Award, Shield, Brain, ArrowRight, ExternalLink, Clock,
    CheckCircle, Loader2, Lock, Trophy, Briefcase, ChevronDown, ChevronUp
} from 'lucide-react';
import { credentialsAPI, careerAPI } from '../utils/api';
import LoadingSpinner from './LoadingSpinner';

const CredentialsDashboard = ({ roadmaps = [], user }) => {
    const navigate = useNavigate();
    const [credentials, setCredentials] = useState([]);
    const [attempts, setAttempts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedRoadmaps, setExpandedRoadmaps] = useState({});

    useEffect(() => {
        const load = async () => {
            try {
                // Fetch both credentials and attempts to determine state
                const [credsData, attemptsData] = await Promise.all([
                    credentialsAPI.getMyCredentials(),
                    credentialsAPI.getMyAttempts()
                ]);
                setCredentials(credsData?.credentials || []);
                setAttempts(attemptsData?.attempts || []);
            } catch (err) {
                console.error("Failed to load credentials data", err);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    // Auto-expand all roadmaps on load
    useEffect(() => {
        if (roadmaps.length > 0) {
            const expanded = {};
            roadmaps.forEach(rm => { expanded[rm.id] = true; });
            setExpandedRoadmaps(expanded);
        }
    }, [roadmaps]);

    const toggleRoadmap = (id) => {
        setExpandedRoadmaps(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const formatDate = (dateValue) => {
        if (!dateValue) return '';
        const d = dateValue._seconds ? new Date(dateValue._seconds * 1000) : new Date(dateValue);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    // Build per-roadmap grouped data
    const roadmapGroups = roadmaps.map(rm => {
        // Fallback for older roadmap data structure
        const careerPath = rm.careerPath || rm.career_path || 'General';
        const weeks = rm?.roadmapData?.weeks || [];
        const completedTaskIds = new Set(rm?.completedTaskIds || []);

        // 1. EARNED CREDENTIALS
        // Filter credentials that belong to this roadmap.
        // Priority: Match by roadmap_id (precise), fallback to career_path (loose)
        const earnedCreds = credentials.filter(c => {
            if (c.roadmap_id) return c.roadmap_id === rm.id;
            return c.career_path === careerPath;
        });

        // 2. AVAILABLE ASSESSMENTS
        const availableAssessments = [];

        weeks.forEach((week, wIdx) => {
            const tasks = week?.tasks || [];
            const totalTasks = tasks.length;
            if (totalTasks === 0) return;

            // Check if week is completed (all tasks marked checks)
            let completedCount = 0;
            tasks.forEach((_, tIdx) => {
                if (completedTaskIds.has(`week_${wIdx}_task_${tIdx}`)) {
                    completedCount++;
                }
            });

            if (completedCount >= totalTasks) {
                const weekTitle = week.title || `Week ${wIdx + 1}`;

                // Check if already earned a credential for this specific week
                const hasCredential = earnedCreds.some(c =>
                    (c.week_index !== undefined && c.week_index === wIdx) ||
                    (c.week_title === weekTitle)
                );

                if (!hasCredential) {
                    // Check latest attempt status (for cooldown / passed state)
                    const latestAttempt = attempts.find(a =>
                        a.roadmap_id === rm.id && a.week_index === wIdx
                    );

                    // If passed, we don't show "Take Assessment" (it will likely show as Credential soon)
                    const isPassed = latestAttempt?.passed === true;

                    if (!isPassed) {
                        availableAssessments.push({
                            roadmapId: rm.id,
                            weekIndex: wIdx,
                            weekTitle,
                            attempt: latestAttempt // contains cooldown details
                        });
                    }
                }
            }
        });

        return {
            id: rm.id,
            careerPath,
            totalWeeks: weeks.length,
            earnedCreds,
            availableAssessments,
            progressPct: rm?.progress?.progressPercentage || 0,
        };
    }).filter(g => g.earnedCreds.length > 0 || g.availableAssessments.length > 0 || g.totalWeeks > 0);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <LoadingSpinner />
            </div>
        );
    }

    const totalCreds = credentials.length;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-500/10 rounded-xl flex items-center justify-center">
                        <Award className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                        <h2 className="text-white font-bold text-sm">Verified Credentials</h2>
                        <p className="text-slate-500 text-[11px] font-mono">
                            {totalCreds} credential{totalCreds !== 1 ? 's' : ''} earned · {roadmapGroups.length} roadmap{roadmapGroups.length !== 1 ? 's' : ''}
                        </p>
                    </div>
                </div>
            </div>

            {/* Roadmap groups */}
            {roadmapGroups.map((group, gIdx) => (
                <motion.div
                    key={group.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: gIdx * 0.1 }}
                    className="bg-[#0F131F] border border-white/5 rounded-2xl overflow-hidden"
                >
                    {/* Roadmap header — clickable to expand/collapse */}
                    <button
                        onClick={() => toggleRoadmap(group.id)}
                        className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/[0.02] transition-colors"
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-gradient-to-br from-purple-500/10 to-violet-500/9 rounded-lg flex items-center justify-center">
                                <Briefcase className="w-4 h-4 text-purple-400" />
                            </div>
                            <div className="text-left">
                                <p className="text-white font-bold text-sm">{group.careerPath}</p>
                                <p className="text-[10px] text-slate-500 font-mono">
                                    {group.earnedCreds.length} earned · {group.availableAssessments.length} available · {group.totalWeeks} weeks total
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            {/* Mini progress ring */}
                            {group.earnedCreds.length > 0 && (
                                <div className="flex items-center gap-1.5 bg-emerald-500/10 px-2 py-0.5 rounded-full">
                                    <Trophy className="w-3 h-3 text-emerald-400" />
                                    <span className="text-[10px] font-mono text-emerald-400">{group.earnedCreds.length}</span>
                                </div>
                            )}
                            {expandedRoadmaps[group.id]
                                ? <ChevronUp className="w-4 h-4 text-slate-500" />
                                : <ChevronDown className="w-4 h-4 text-slate-500" />
                            }
                        </div>
                    </button>

                    {/* Expanded content */}
                    {expandedRoadmaps[group.id] && (
                        <div className="border-t border-white/5 px-5 py-4 space-y-4">
                            {/* Earned credentials for this roadmap */}
                            {group.earnedCreds.length > 0 && (
                                <div className="space-y-2">
                                    <h4 className="text-[10px] font-mono text-slate-500 uppercase">Earned Credentials</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {group.earnedCreds.map((cred) => (
                                            <div
                                                key={cred.id}
                                                className="bg-gradient-to-br from-[#1a1040]/40 via-[#0B0F19] to-[#0a1628]/40 border border-purple-500/10 rounded-xl p-4 space-y-2 hover:border-purple-500/25 transition-colors group"
                                            >
                                                <div className="flex items-start justify-between">
                                                    <div className="flex items-center gap-2.5">
                                                        <div className="w-8 h-8 bg-purple-500/10 rounded-lg flex items-center justify-center group-hover:bg-purple-500/20 transition-colors">
                                                            <Trophy className="w-3.5 h-3.5 text-purple-400" />
                                                        </div>
                                                        <div>
                                                            <p className="text-white font-bold text-[13px]">{cred.skill_area}</p>
                                                            <p className="text-[10px] text-slate-500 font-mono">{cred.week_title}</p>
                                                        </div>
                                                    </div>
                                                    <span className="text-purple-400 font-black text-lg">{cred.score}%</span>
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3 text-[10px] text-slate-500 font-mono">
                                                        <span className="flex items-center gap-1">
                                                            <CheckCircle className="w-3 h-3 text-emerald-400" />
                                                            Verified
                                                        </span>
                                                        <span>{formatDate(cred.issued_at)}</span>
                                                    </div>
                                                    <a
                                                        href={`/verify/${cred.id}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-slate-500 hover:text-purple-400 transition-colors"
                                                    >
                                                        <ExternalLink className="w-3.5 h-3.5" />
                                                    </a>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Available assessments for this roadmap */}
                            {group.availableAssessments.length > 0 && (
                                <div className="space-y-2">
                                    <h4 className="text-[10px] font-mono text-slate-500 uppercase">Available Assessments</h4>
                                    <div className="space-y-2">
                                        {group.availableAssessments.map((item) => {
                                            const isCooldown = item.attempt?.in_cooldown;
                                            const cooldownHours = item.attempt?.cooldown_remaining_hours;

                                            return (
                                                <div
                                                    key={`${item.roadmapId}-${item.weekIndex}`}
                                                    className="bg-[#0B0F19] border border-white/5 rounded-lg p-3 flex items-center justify-between hover:border-purple-500/15 transition-colors"
                                                >
                                                    <div className="flex items-center gap-2.5">
                                                        <div className={`w-7 h-7 rounded-md flex items-center justify-center ${isCooldown ? 'bg-amber-500/10' : 'bg-emerald-500/10'}`}>
                                                            {isCooldown ? (
                                                                <Clock className="w-3.5 h-3.5 text-amber-400" />
                                                            ) : (
                                                                <Brain className="w-3.5 h-3.5 text-emerald-400" />
                                                            )}
                                                        </div>
                                                        <div>
                                                            <p className="text-white text-sm font-medium">{item.weekTitle}</p>
                                                            {isCooldown && (
                                                                <p className="text-[10px] text-amber-400/80 font-mono">
                                                                    Next attempt in {cooldownHours}h
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {isCooldown ? (
                                                        <button
                                                            disabled
                                                            className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-slate-500 text-[11px] font-medium cursor-not-allowed"
                                                        >
                                                            Cooldown Active
                                                        </button>
                                                    ) : (
                                                        <button
                                                            onClick={() => navigate(`/assessment?roadmap=${item.roadmapId}&week=${item.weekIndex}`)}
                                                            className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-500/10 border border-purple-500/20 rounded-lg text-purple-400 text-[11px] font-medium hover:bg-purple-500/20 transition-colors"
                                                        >
                                                            Take Assessment
                                                            <ArrowRight className="w-3 h-3" />
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* No completed weeks for this roadmap */}
                            {group.earnedCreds.length === 0 && group.availableAssessments.length === 0 && (
                                <p className="text-slate-500 text-xs text-center py-3">
                                    Complete a week to unlock assessments for this roadmap
                                </p>
                            )}
                        </div>
                    )}
                </motion.div>
            ))}

            {/* Empty state — no roadmaps at all */}
            {roadmapGroups.length === 0 && (
                <div className="bg-[#0F131F] border border-white/5 rounded-2xl p-10 text-center space-y-4">
                    <div className="w-16 h-16 bg-purple-500/10 rounded-2xl flex items-center justify-center mx-auto">
                        <Lock className="w-8 h-8 text-purple-400/50" />
                    </div>
                    <div className="space-y-2">
                        <h3 className="text-white font-bold">No Credentials Yet</h3>
                        <p className="text-slate-500 text-sm max-w-sm mx-auto">
                            Complete a week in your roadmap to unlock skill assessments. Pass with ≥70% to earn a verified, shareable credential.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CredentialsDashboard;
